<script setup lang="ts">
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import type { ChampionshipRegistrationSelectTeamTask } from "@/model/championship/championship-registration-select-team-task"
import { ChampionshipTeamUploadState } from "@/model/championship/championship-team-upload-state"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { $date, createInlineTable, useHttpGet, useToggleableValue } from "@/vf"
import { computed, provide, toRefs } from "vue"
import { type ChampionshipRegistrationFinalizeTeamData, SelectTeamDataInjection } from "./championship-registration"
import ChampionshipRegistrationAdditionalCostsSectionAdmin from "./internal/ChampionshipRegistrationAdditionalCostsSectionAdmin.vue"
import ChampionshipRegistrationCancelMembersContent from "./internal/ChampionshipRegistrationCancelMembersContent.vue"
import ChampionshipRegistrationCancelTeamRequestSection from "./internal/ChampionshipRegistrationCancelTeamRequestSection.vue"
import ChampionshipRegistrationChangeRequestList from "./internal/ChampionshipRegistrationChangeRequestList.vue"
import ChampionshipRegistrationEditCategoryWizard from "./internal/ChampionshipRegistrationEditCategoryWizard"
import ChampionshipRegistrationEditContactPerson from "./internal/ChampionshipRegistrationEditContactPerson.vue"
import ChampionshipRegistrationEditMembersCountAdmin from "./internal/ChampionshipRegistrationEditMembersCountAdmin.vue"
import ChampionshipRegistrationEditTeamFinalization from "./internal/ChampionshipRegistrationEditTeamFinalization.vue"
import ChampionshipRegistrationFileUpload from "./internal/ChampionshipRegistrationFileUpload.vue"
import ChampionshipRegistrationResetButton from "./internal/ChampionshipRegistrationResetButton.vue"
import ChampionshipRegistrationTeamList from "./internal/ChampionshipRegistrationTeamList.vue"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const { data: data, refresh } = await useHttpGet<ChampionshipRegistrationFinalizeTeamData>(
    `/championship/registration/${task.value.organizationId}/finalize-teams/${task.value.id}`,
)

const [opened, open_] = useToggleableValue<string>()

provide(SelectTeamDataInjection, data)
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)
function open(section: string | null, isAllowed: boolean = true) {
    if (!isAllowed) {
        return
    }

    open_(section)
}

const inlineTable = createInlineTable({
    closed(reason) {
        if (reason.saved) {
            refresh()
            emit("updated")
        }
    },
})

const trans = defineTranslations({
    todo_title: {
        de: "Folgende Dinge müssen noch erledigt werden, bevor ihr zur Meisterschaft starten könnt:",
        en: "The following things need to be done before you can start the championship:",
    },
    todo: {
        de: "Muss bis zum {date} erledigt werden",
        en: "Must be done by {date}",
    },
    in_review: {
        de: "Die Datei wird von uns geprüft.",
        en: "The file is being reviewed by us.",
    },
    approved: {
        de: "Die Datei wurde akzeptiert.",
        en: "The file has been approved.",
    },
    rejected: {
        de: "Die Datei wurde abgelehnt. Bitte ladet die Datei erneut hoch.",
        en: "The file has been rejected. Please upload a new file.",
    },
    file_task_name: {
        de: "{fileName} hochladen",
        en: "Upload {fileName}",
    },
    file_optional: {
        de: "(optional)",
        en: "(optional)",
    },
    name_all_participants: {
        de: "Alle Teilnehmer namentlich melden",
        en: "Report all participants by name",
    },
    appoint_safety_champion: {
        de: "Safety champion benennen",
        en: "Appoint a safety champion",
    },
    appoint_accuscore_member: {
        de: "Accuscore-Ansprechperson benennen",
        en: "Appoint an accuscore member",
    },
    invalid_safety_champion: {
        de: "Dein Safety Champion ist ungültig. Bitte prüfe, ob die Person in deinem Team ist.",
        en: "Your safety champion is invalid. Please check if the person is in your team.",
    },
    invalid_accuscore_member: {
        de: "Deine Accuscore-Ansprechperson ist ungültig. Bitte prüfe, ob die Person in deinem Team ist.",
        en: "Your accuscore member is invalid. Please check if the person is in your team.",
    },
    edit_members_count: {
        de: "Anzahl der Teammitglieder anpassen",
        en: "Adjust number of team members",
    },
    members_count_description: {
        de: "{participants} Starter, {alternates} Ersatz, {entourages} Coaches und Begleitpersonen",
        en: "{participants} participants, {alternates} alternates, {entourages} entourages",
    },
    checked_in_locked: {
        de: "Die Teams wurden bereits eingecheckt. Änderungen sind nicht mehr möglich.",
        en: "This registration has already checked in. Changes are no longer possible.",
    },
    checked_in_additional_costs_only: {
        de: "Die Teams wurden bereits eingecheckt. Nur zusätzliche Kosten können noch verwaltet werden, solange noch keine Rechnung erstellt wurde.",
        en: "Teams are already checked in. Only additional costs can still be managed as long as no invoice has been created.",
    },
    change_category: {
        de: "Kategorie ändern",
        en: "Change category",
    },
    cancel_members: {
        de: "Personen stornieren",
        en: "Cancel persons",
    },
    cancel_members_description: {
        de: "Einzelne Personen oder freie Plätze aus dem Team stornieren",
        en: "Cancel single persons or blank slots from the team",
    },
    current_category: {
        de: "Aktuelle Kategorie: {name}",
        en: "Current category: {name}",
    },
})
</script>
<template>
    <div class="p-5">
        <div v-if="data?.isCheckedIn" class="alert alert-warning">
            {{ policy.CreateAdditionalCost ? trans("checked_in_additional_costs_only") : trans("checked_in_locked") }}
        </div>

        <div v-if="data?.teams.length ?? 0 > 0">
            <div class="alert alert-info" v-if="viewMode === 'admin'">
                {{ $t("@tasks:tasks.championship_registration.finalize_teams.waiting_for_customer") }}
            </div>
            <div class="mb-3" v-else>
                {{ $t("@tasks:tasks.championship_registration.finalize_teams.description") }}
            </div>

            <ChampionshipRegistrationTeamList
                :registration-id="data!.registrationId"
                :teams="data!.teams"
                :view-mode="props.viewMode"
                :permissions="data?.permissions"
            >
                <template #secondaryRow="{ team }">
                    <tr>
                        <td colspan="100%" class="pl-5 pb-5">
                            <div class="mb-3">{{ trans("todo_title") }}</div>

                            <div v-for="file in data?.teamTodos[team.id!].files" :key="file.id">
                                <button
                                    type="button"
                                    class="wizard-button font-weight-normal"
                                    :class="{ dark: opened === `file/${team.id}/${file.id}` }"
                                    @click="open(`file/${team.id}/${file.id}`, policy.UploadTeamFile)"
                                    :disabled="!policy.UploadTeamFile"
                                >
                                    <div class="d-flex justify-content-start align-items-center">
                                        <div class="mr-3">
                                            <i
                                                class="far fa-fw fa-square"
                                                v-if="file.state === ChampionshipTeamUploadState.Missing"
                                            ></i>
                                            <i
                                                class="fas fa-fw fa-check"
                                                v-if="
                                                    file.state === ChampionshipTeamUploadState.Pending ||
                                                    file.state === ChampionshipTeamUploadState.Approved
                                                "
                                            ></i>
                                            <i
                                                class="fas fa-fw fa-xmark"
                                                v-if="file.state === ChampionshipTeamUploadState.Rejected"
                                            ></i>
                                        </div>
                                        <div class="d-flex flex-column">
                                            <b>
                                                {{ trans("file_task_name", { fileName: file.name }) }}
                                                <span v-if="!file.mandatory" class="font-weight-normal">
                                                    {{ trans("file_optional") }}
                                                </span>
                                            </b>
                                            <span v-if="file.state === ChampionshipTeamUploadState.Missing">
                                                {{ trans("todo", { date: $date(file.deadline) }) }}
                                            </span>
                                            <span v-if="file.state === ChampionshipTeamUploadState.Pending">
                                                {{ trans("in_review") }}
                                            </span>
                                            <span v-if="file.state === ChampionshipTeamUploadState.Approved">
                                                {{ trans("approved") }}
                                            </span>
                                            <span v-if="file.state === ChampionshipTeamUploadState.Rejected">
                                                {{ trans("rejected") }}
                                            </span>
                                        </div>
                                    </div>
                                </button>

                                <ChampionshipRegistrationFileUpload
                                    v-if="opened === `file/${team.id}/${file.id}`"
                                    :registration-id="data!.registrationId"
                                    :team="team"
                                    :file="file"
                                    :view-mode="props.viewMode"
                                    @updated="refresh(), emit('updated')"
                                />
                            </div>

                            <button
                                type="button"
                                class="wizard-button font-weight-normal"
                                :class="{ dark: opened === `safetyChampion/${team.id}` }"
                                @click="open(`safetyChampion/${team.id!}`, policy.ChangeTeamContactPerson)"
                                :disabled="!policy.ChangeTeamContactPerson"
                            >
                                <div class="d-flex justify-content-start align-items-center">
                                    <div class="mr-3">
                                        <i
                                            class="far fa-fw fa-square"
                                            v-if="data?.teamTodos[team.id!].safetyChampionValid.state === 'pending'"
                                        ></i>
                                        <i
                                            class="fas fa-fw fa-check"
                                            v-if="data?.teamTodos[team.id!].safetyChampionValid.state === 'ok'"
                                        ></i>
                                        <i
                                            class="fas fa-fw fa-xmark"
                                            v-if="data?.teamTodos[team.id!].safetyChampionValid.state === 'invalid'"
                                        ></i>
                                    </div>
                                    <div class="d-flex flex-column">
                                        <b v-if="data?.teamTodos[team.id!].safetyChampionValid.state === 'invalid'">
                                            {{ trans("invalid_safety_champion") }}
                                        </b>
                                        <b v-else>
                                            {{ trans("appoint_safety_champion") }}
                                        </b>
                                        <span>
                                            {{ trans("todo", { date: $date(data?.deadline) }) }}
                                        </span>
                                    </div>
                                </div>
                            </button>

                            <div v-if="opened === `safetyChampion/${team.id}`">
                                <ChampionshipRegistrationEditContactPerson
                                    :team-id="team.id!"
                                    which="safetyChampion"
                                    :data="data!"
                                    @updated="refresh(), open(null)"
                                />
                            </div>

                            <template v-if="data?.teamTodos[team.id!].categoryRequiresAccuscore">
                                <button
                                    type="button"
                                    class="wizard-button font-weight-normal"
                                    :class="{ dark: opened === `accuscore/${team.id}` }"
                                    @click="open(`accuscore/${team.id!}`, policy.ChangeTeamContactPerson)"
                                    :disabled="!policy.ChangeTeamContactPerson"
                                >
                                    <div class="d-flex justify-content-start align-items-center">
                                        <div class="mr-3">
                                            <i
                                                class="far fa-fw fa-square"
                                                v-if="data?.teamTodos[team.id!].accuscoreMemberValid.state === 'pending'"
                                            ></i>
                                            <i
                                                class="fas fa-fw fa-check"
                                                v-if="data?.teamTodos[team.id!].accuscoreMemberValid.state === 'ok'"
                                            ></i>
                                            <i
                                                class="fas fa-fw fa-xmark"
                                                v-if="data?.teamTodos[team.id!].accuscoreMemberValid.state === 'invalid'"
                                            ></i>
                                        </div>
                                        <div class="d-flex flex-column">
                                            <b
                                                v-if="data?.teamTodos[team.id!].accuscoreMemberValid.state === 'invalid'"
                                            >
                                                {{ trans("invalid_accuscore_member") }}
                                            </b>
                                            <b v-else>
                                                {{ trans("appoint_accuscore_member") }}
                                            </b>
                                            <span>
                                                {{ trans("todo", { date: $date(data?.deadline) }) }}
                                            </span>
                                        </div>
                                    </div>
                                </button>

                                <div v-if="opened === `accuscore/${team.id}`">
                                    <ChampionshipRegistrationEditContactPerson
                                        :team-id="team.id!"
                                        which="accuscore"
                                        :data="data!"
                                        @updated="refresh(), open(null)"
                                    />
                                </div>
                            </template>

                            <button
                                type="button"
                                class="wizard-button font-weight-normal"
                                :class="{ dark: opened === `members/${team.id}` }"
                                @click="
                                    open(
                                        `members/${team.id!}`,
                                        policy.FinalizeTeamsUpdateTeam || policy.ChangeRequestCreateMembers,
                                    )
                                "
                                :disabled="!policy.FinalizeTeamsUpdateTeam && !policy.ChangeRequestCreateMembers"
                            >
                                <div class="d-flex justify-content-start align-items-center">
                                    <div class="mr-3">
                                        <i class="far fa-fw fa-square" v-if="!data?.teamTodos[team.id!].blockers"></i>
                                        <i class="fas fa-fw fa-check" v-if="data?.teamTodos[team.id!].blockers"></i>
                                    </div>
                                    <div class="d-flex flex-column">
                                        <b>
                                            {{ trans("name_all_participants") }}
                                        </b>
                                        <span>
                                            {{ trans("todo", { date: $date(data?.deadline) }) }}
                                        </span>
                                    </div>
                                </div>
                            </button>

                            <div v-if="opened === `members/${team.id}`">
                                <ChampionshipRegistrationEditTeamFinalization
                                    :action="policy.FinalizeTeamsUpdateTeam ? 'finalize' : 'changeRequest'"
                                    :team="team"
                                    :data="data!"
                                    @updated="refresh(), open(null)"
                                />
                            </div>

                            <template v-if="data?.isRegistrationClosed">
                                <button
                                    type="button"
                                    class="wizard-button font-weight-normal"
                                    :class="{ dark: opened === `category/${team.id}` }"
                                    @click="open(`category/${team.id!}`)"
                                    :disabled="
                                        !policy.ChangeRequestCreateCategory &&
                                        !policy.ChangeTeamCategory &&
                                        !policy.ChangeRequestCreateCategoryWithMembers &&
                                        !policy.ChangeTeamCategoryWithMembers
                                    "
                                >
                                    <div class="d-flex flex-column">
                                        <b>
                                            {{ trans("change_category") }}
                                        </b>
                                        <span>
                                            {{ trans("current_category", { name: team.championshipCategory?.name }) }}
                                        </span>
                                    </div>
                                </button>

                                <div v-if="opened === `category/${team.id}`" class="px-4 pb-4">
                                    <ChampionshipRegistrationEditCategoryWizard
                                        :registration-id="data!.registrationId"
                                        :team="team"
                                        mode="changeRequest"
                                        @save="refresh(), open(null)"
                                    />
                                </div>
                            </template>

                            <button
                                type="button"
                                class="wizard-button font-weight-normal"
                                :class="{ dark: opened === `cancelMembers/${team.id}` }"
                                @click="open(`cancelMembers/${team.id!}`, policy.CancelTeamMembers)"
                                :disabled="!policy.CancelTeamMembers"
                            >
                                <div class="d-flex flex-column">
                                    <b>{{ trans("cancel_members") }}</b>
                                    <span>{{ trans("cancel_members_description") }}</span>
                                </div>
                            </button>

                            <div v-if="opened === `cancelMembers/${team.id}`" class="px-2">
                                <ChampionshipRegistrationCancelMembersContent
                                    :registration-id="data!.registrationId"
                                    :team="team"
                                    @updated="refresh(), emit('updated'), open(null)"
                                />
                            </div>

                            <ChampionshipRegistrationCancelTeamRequestSection
                                :registration-id="data!.registrationId"
                                :team="team"
                                @updated="refresh(), emit('updated'), open(null)"
                            />

                            <!-- Admin only: Edit members count -->
                            <div
                                v-if="policy.UpdateFinalizationMembersCount"
                                class="d-flex align-items-center py-3 border-top border-gray uploadable"
                                @click="open(`membersCount/${team.id!}`, policy.UpdateFinalizationMembersCount)"
                            >
                                <div class="mx-3">
                                    <i class="fas fa-fw fa-users-cog text-primary"></i>
                                </div>
                                <div>
                                    <div class="font-weight-bold">
                                        {{ trans("edit_members_count") }}
                                    </div>
                                    <div class="text-muted">
                                        {{
                                            trans("members_count_description", {
                                                participants: team.numberOfParticipants ?? 0,
                                                alternates: team.numberOfAlternates ?? 0,
                                                entourages: team.numberOfEntourages ?? 0,
                                            })
                                        }}
                                    </div>
                                </div>
                            </div>

                            <div v-if="opened === `membersCount/${team.id}`">
                                <ChampionshipRegistrationEditMembersCountAdmin
                                    :registration-id="data!.registrationId"
                                    :team="team"
                                    @updated="refresh(), emit('updated'), open(null)"
                                />
                            </div>

                            <ChampionshipRegistrationAdditionalCostsSectionAdmin
                                :registration-id="data!.registrationId"
                                :team="team"
                                :additional-costs="data?.teamAdditionalCosts?.[team.id!] ?? []"
                                @updated="refresh(), emit('updated')"
                            />

                            <div class="mt-4">
                                <ChampionshipRegistrationChangeRequestList
                                    v-for="changeRequest of data?.changeRequests.filter(cr => cr.teamId === team.id)"
                                    :change-request="changeRequest"
                                    :team="team"
                                    :inline-table="inlineTable"
                                    :key="changeRequest.id"
                                />
                            </div>
                        </td>
                    </tr>
                </template>
            </ChampionshipRegistrationTeamList>
        </div>
        <div v-else>{{ $t("@tasks:tasks.championship_registration.finalize_teams.complete") }}</div>

        <VarsityInlineConfirmGroup>
            <ChampionshipRegistrationResetButton
                :registrationId="data!.registrationId"
                :view-mode="props.viewMode"
                @resetted="emit('updated')"
                #default="{ renderButton, renderConfirm }"
            >
                <div class="d-flex justify-content-between mt-5" v-if="props.task.state === 'open'">
                    <div class="flex-grow-1">
                        <component :is="renderButton()" v-if="policy.ResetRegistration" />
                    </div>
                </div>
                <div>
                    <component :is="renderConfirm" />
                </div>
            </ChampionshipRegistrationResetButton>
        </VarsityInlineConfirmGroup>
    </div>
</template>

<style scoped>
.uploadable {
    cursor: pointer;
}

.uploadable:hover {
    background-color: var(--light);
}
</style>
